import {
  Currency,
  CustomVendorList,
  MediaSubtype,
} from '@accedo/one-plugin-sdk';
import {
  Action,
  DataSource,
  FetchResult,
  IconNames,
  Item as DynUiItem,
} from '@dynamic/ui';
import { AxiosRequestConfig } from 'axios';
import { ResourceKey } from 'i18next';
import { SWRConfiguration } from 'swr';

import { ButtonVariant, FormFactors } from '#/service/constants';
import { MenuItemDisplayType } from '#/service/constants/menu';

/**
 * Core response `type` values for the somewhat unified Form and Auth-related endpoints.
 * Some other endpoints (like UpNext) have unique response types and `_meta` object, and are not covered here.
 * @see: https://docs.one.accedo.io/one-core/delivery-api/identity/common-input-fields/#common-fields
 */
export enum CoreResponseType {
  // form submit responses
  SUCCESS = 'success',
  ERROR = 'error',
  NOTIFICATION = 'notification',
  // form fields responses
  INPUT = 'input',
}

type MetaErrorName = 'accountBalances' | 'entitlements';
type MetaError = { message: string; status: number };

/**
 * Utility type to create Core response types for the unified Form and Auth-related endpoints.
 * Some other endpoints (like UpNext) have unique response types and `_meta` object, and are not covered here.
 * Two main kinds: submit (with `error`) and general (with `errors`).
 * Additional meta attributes can be passed in.
 * Additional root-level attributes can be added by extending.
 * Meant to be a local utility, try not to export it.
 */
type CoreResponse<
  Kind extends 'submit' | 'general',
  AdditionalMeta extends Record<string, unknown> = {},
> = {
  _meta: (Kind extends 'submit'
    ? // for submit responses
      {
        type: CoreResponseType;
        error?: {
          // field keys mapped to array of validation messages
          validation?: Record<string, string[]>;
        };
        message?: string;
        // actual response will never include this, but we need it for `CommonForm`
        ignoreError?: boolean;
        errors?: never;
      }
    : // for general responses
      {
        type?: CoreResponseType;
        errors?: Record<MetaErrorName, MetaError>;
        error?: never;
        message?: never;
        ignoreError?: never;
      }) &
    AdditionalMeta;
};

/**
 * Response after submitting a common form.
 * Returned by POST requests to common form endpoints.
 */
export interface SubmitResponse extends CoreResponse<'submit'> {}

/**
 * The contents (fields, headings, etc.) to display on a common form.
 * Returned by GET requests to common form endpoints.
 */
export interface FieldsResponse extends CoreResponse<'general'> {
  display: InputApiField[];
}

export interface LoginRequest {
  deviceInfo: DeviceInfo;
  values: LoginRequestValues;
  secondScreen?: {
    code: string;
  };
}

export interface LoginRequestValues {
  signInEmailAddress: string;
  currentPassword: string;
}

export interface DeviceInfoRequest {
  deviceInfo: DeviceInfo;
}
export interface DeviceInfo {
  id: string;
  hardware: {
    manufacturer: string;
    model: string;
    version: string;
  };
  os: {
    name: string;
    version: string;
  };
  display: {
    width: number;
    height: number;
    formFactor: Uppercase<FormFactors> | 'UNKNOWN';
  };
  legal?: {
    tcfv2?: string;
    customVendors?: CustomVendorList;
  };
}

export interface Platform {
  // fields from specification
  type: 'Web'; // always 'Web' for the web app
  make?: string;
  model?: string;
  os?: string;
  osVersion?: string;
  resolution?: string;
  uuid?: string;

  // added for backwards compatibility for Settings
  browser?: string;
}

export type TFormData = { [key: string]: string | boolean | undefined };

export interface RegisterRequest {
  deviceInfo: DeviceInfo;
  values: TFormData;
}

export interface AuthResult
  extends CoreResponse<
    'submit',
    {
      auth: TokenResponse;
      secondScreen?: { redirectUrl?: string };
    }
  > {}

export const isAuthResult = (
  res: AuthResult | SubmitResponse
): res is AuthResult => 'auth' in res._meta;

export type AuthCodeRequestType = 'AUTHENTICATION' | 'REGISTRATION';
export enum AuthCodeStatus {
  Pending = 'PENDING_SECOND_SCREEN_ACTION',
  Canceled = 'CANCELED',
  Expired = 'EXPIRED',
  Completed = 'FLOW_COMPLETED',
}

export interface AuthCodeCheckResponse {
  codeEntry: AuthCodeEntry;
  /** Contains parts of the deviceInfo structure from the primary device. This may be used by the second screen to display hints to the user like "To login on your *Roku* device, enter your credentials here" */
  primaryDeviceInfo?: {
    manufacturer: string;
    operatingSystem: string;
  };
  status: AuthCodeStatus;
}

interface AuthCodeEntry {
  /** The authentication code used for the Second Screen Authentication flow */
  code: string;

  /** When authentication code expires */
  expiresAt: string;

  /** The type of flow the primary device wants to trigger. Only login is supported at this time */
  requestType: AuthCodeRequestType;
}

export enum AuthenticationType {
  None = 'none',
  Standard = 'standard',
  TVE = 'tve',
  Anonymous = 'anonymous',
}

export type Greeting =
  | {
      message: string;
      show: 'ALWAYS';
    }
  | {
      message: string;
      show: 'IF_LAST_SHOWN_BEFORE';
      // both variables below contains current time - minimum greeting interval set in Admin.
      ifShownBefore: string; // ISO date string
      ifShownBeforeEpoch: number; // epoch time in seconds
    };

export interface TokenResponse {
  token: string;
  refreshToken: string;
  expiration: string;
  principal?: {
    userId: string;
  };
  greeting?: Greeting;
  preferences?: {
    prompt?: boolean;
  };
}

export interface OauthTokenResponse {
  oauth?: {
    accessToken?: string;
  };
}

export interface ProfileResponse extends CoreResponse<'general'> {
  userId: string | undefined;
  authentication: Authentication;
  entitlements: Entitlements;
  profile: Profile;
  subscriptions?: Subscription[];
  subscriber?: boolean;
}

// Preference types
// - these types are added just for starters according to preferences feature
// - these should be handled as form input definitions types later
type PreferenceType = 'static' | 'input';
type PreferenceInputVariant = 'checkbox' | 'multi_select' | 'select';
type PreferenceStaticVariant = 'heading' | 'image' | 'checkbox';
type SelectDisposition = 'GRID' | 'DROPDOWN';

export interface PreferencesResponse extends CoreResponse<'general'> {
  display?: PreferenceDisplay[];
}

// only added multi-select as we are only using this type yet
type PreferenceDisplay = MultiSelectDisplay;

interface PreferenceDisplayBase {
  type: PreferenceType;
  variant: PreferenceInputVariant | PreferenceStaticVariant;
}

export interface MultiSelectDisplay extends PreferenceDisplayBase {
  key: string;
  label: string;
  description: string;
  displayConfiguration?: {
    disposition?: SelectDisposition;
  };
  required: boolean;
  values: MultiSelectOption[];
}

export interface MultiSelectOption {
  key: string;
  label: string;
  imageUrl?: string;
  color?: string;
  value: any;
}

export interface AccountBalance {
  amount: number;
  currency: Currency;
}

export interface Profile {
  attributes: {
    [key: string]: any;
    mvpdId?: string;
  };
  accountBalances?: AccountBalance[];
  preferences?: Preferences;
  // Per-user resolved configuration.
  configuration?: ProfileConfiguration;
  device?: { network?: { ip?: string } };
}

export interface ParentalRatingScheme {
  type: string;
  values: string[];
}

export interface ProfileParentalControl {
  parentalPin?: {
    enabled?: boolean;
    length?: number;
    preferenceKey?: string;
    restrictedContent?: {
      level?: string;
      preferenceKey?: string;
      ratings?: ParentalRatingScheme[];
    };
  };
  preferredParentalRatingSchemes?: string[];
}

export interface ProfileConfiguration {
  parentalControl?: ProfileParentalControl;
}

export interface Preferences {
  [key: string]: any;
}

export interface Entitlements {
  subscriber: boolean;
  canDownloadVideos?: boolean;
  subscriptions?: Subscription[];
  grantedEntitlementTags?: string[];
}

export type PurchaseMethod =
  | 'CREDIT_CARD'
  | 'IAP_AMAZON'
  | 'IAP_APPLE'
  | 'IAP_GOOGLE'
  | 'IAP_ROKU'
  | 'PAYPAL'
  | 'UNKNOWN';

interface ProductDetails {
  // this is technically a userProductId
  productId: string;
}

export interface Subscription {
  id: string;
  label: string;
  purchaseMethod: PurchaseMethod;
  purchaseMethodTranslationKey: string;
  creditCardProductDetails?: ProductDetails;
  paypalProductDetails?: ProductDetails;
  subscriptionGroupId?: string;
}

export interface Authentication {
  type: AuthenticationType;
  expiration: string;
}

export interface FavoritesResponse {
  data?: FavoriteItem[];
}

// `video.watched` object in item and history responses
export interface Watched {
  date?: string;
  progress?: {
    percentage: number;
    seconds: number;
    finished: boolean;
  };
}

// one item in the `watch-history/all` response
export interface HistoryAllItem {
  objectId: string;
  objectType: MediaType;
  watched?: Watched;
}

// `watch-history/all` response
export interface HistoryAllResponse {
  data: HistoryAllItem[];
}

export interface FavoriteItem {
  objectId: string;
  objectType: MediaType;
}

export interface Locale {
  endonym: string;
  exonym: string;
  code: string;
  fallback?: boolean;
}

export interface TranslationResponse {
  translations: ResourceKey;
}
export interface LocalesResponse {
  locales: Locale[];
}

export enum SensitiveAction {
  ParentalPinUpdate = 'parental_pin_update',
  SensitiveProfileFieldUpdate = 'sensitive_profile_field_update',
}

export enum DateGranularity {
  DateOnly = 'DATE_ONLY',
  DateAndTime = 'DATE_AND_TIME',
  TimeOnly = 'TIME_ONLY',
}

export type DisplayConfiguration = {
  multiline?: boolean;
  disposition?: 'DROPDOWN' | 'GRID';
  granularity?: DateGranularity;
  overrideIs24HourClock?: boolean;
};

export interface InputApiField {
  key: string;
  label?: string;
  placeholder?: string;
  description?: string;
  required?: boolean;
  variant?: string;
  linkPath?: string;
  displayConfiguration?: DisplayConfiguration;
  heading?: {
    text: string;
  };
  type: FormInputTypes.INPUT | FormInputTypes.STATIC;
}

export interface ChangeFavoriteRequest {
  id: string;
  type: MediaType;
}

export enum ResourceType {
  Item = 'item',
  Collection = 'collection',
}

type Video = {
  duration?: number | string;
  playback: string;
  accessLevel?: AccessLevel;
  watched?: Watched;
  closedCaptions?: {
    availableLanguages: string[];
  };
  entitlementTags?: string[];
  markers?: {
    fullyWatched: number;
  };
  parentalRatings?: ParentalRating[];
  viewableOffline?: boolean;
};

// created based on `Video` as a start, in preparation of them deviating more and more in the future.
// some props might become irrelevant for audio, others might be renamed to make more sense for audio (like "watched" to "listened").
// we already know that `closedCaptions` do not apply to audio (at least so far), and `parentalRating` will be totally different.
type Audio = {
  duration?: number | string;
  playback: string;
  accessLevel?: AccessLevel;
  watched?: Watched;
  entitlementTags?: string[];
  markers?: {
    fullyWatched: number;
  };
  viewableOffline?: boolean;
};

export interface ParentalRating {
  advisoryCodes?: string[];
  type: string;
  value: string;
}

export enum MediaType {
  Item = 'ITEM',
  Collection = 'COLLECTION',
}

export const mediaTypeToResourceType: Record<MediaType, ResourceType> = {
  [MediaType.Item]: ResourceType.Item,
  [MediaType.Collection]: ResourceType.Collection,
};

// has `video` or `audio` depending on the `subtype`. see `isAudio`.
export interface Item extends DynUiItem, MediaResource {
  video?: Video;
  audio?: Audio;
}

// Represents the same thing as the DynUi `FetchResult`, but DynUi `Item` is not compatible with our `Item`, so we have to overwrite that.
export interface FeedResponse extends FetchResult {
  data?: Item[];
}

export type PageInfo = {
  total: number;
  hasMore: boolean;
  page: number;
  pageSize: number;
};

export enum ItemImageKind {
  Poster = 'poster',
  Still = 'still',
  Background = 'background',
  Logo = 'logo',
  Thumbnail = 'thumbnail',
}

export enum ItemImageKey {
  Wide = 'wide',
  '16:9' = '16:9',
  Portrait = 'portrait',
  Landscape = 'landscape',
  Square = 'square',
}

export type ImageVariants = {
  [Key in ItemImageKey]?: ImageDetails[];
};

export type ItemImages = {
  [Kind in ItemImageKind]?: ImageVariants;
};
export interface MediaResource {
  id: string;
  title: string;
  description?: string;
  extras?: object;
  shortDescription?: string;
  releaseDate?: string;
  genres?: string[];
  playback?: string;
  subtype: MediaSubtype;
  refs: ItemRefs;
  images: ItemImages;
  type: MediaType;
  episode?: {
    number: number;
  };
  tvShow?: {
    id: string;
    title: string;
  };
  tvShowSeason?: {
    id: string;
    season: {
      number: number;
      title?: string;
    };
    title?: string;
  };
  show?: {
    seasonCount: number;
  };
  season?: {
    number: number;
    episodeCount: number;
  };
  upNext?: Item;
  availability?: {
    from: string;
    fromEpoch: number;
    until: string;
    untilEpoch: number;
  };
  cast?: any;
  releaseDateEpoch?: number;
  index?: number;
  channelNumber?: number;
  /** The channel that plays this item. Only live programs have it. */
  liveChannel?: EmbeddedLiveChannel;
}

export interface ItemRefs {
  self: string;
  next: string;
  download?: string;
  share?: string;
  tvShow?: string;
  tvShowSeason?: string;
  schedule?: string;
  // playback is not a typical ref returned from one-core, but injected by us from the items video.playback field
  playback?: string;
}

export interface ResourceItem {
  data: Item;
}

export interface SeasonCollection extends MediaResource {
  season?: {
    episodeCount: number;
    number: number;
  };
}

export interface Collection {
  collection: MediaResource;
  data: SeasonCollection[];
}

interface ImageDetails {
  url: string;
  label: string;
  sizes?: ImageSize[];
  blurhash?: string;
}

interface ImageSize {
  width: number;
  height: number;
}

export enum AccessLevel {
  UNRESTRICTED = 'UNRESTRICTED',
  AUTHENTICATION_REQUIRED = 'AUTHENTICATION_REQUIRED',
  ENTITLEMENT_REQUIRED = 'ENTITLEMENT_REQUIRED',
}
interface ItemTemplateRule {
  template: {
    component: {
      properties: {
        action?: {
          params: {
            destination: {
              id: string;
            };
          };
        };
      };
    };
  };
}

export interface ContainerData {
  id: string;
  data: {
    feed: string;
  };
  dataSource?: DataSource;
  itemTemplateRules: ItemTemplateRule[];
  templates?: {
    [key: string]: {
      id: string;
    };
  };
  index?: number;
  visibility?: Condition;
}

export type PageLayout = {
  page: LayoutData;
  templates: V2TemplatesData;
};

type NullableContainerData = ContainerData | null;

export interface CollectionData {
  componentId: string;
  containers: NullableContainerData[];
}

export interface TemplateProps {
  title?: string;
  topContainerShouldNotOverlap?: boolean;
}

export interface LayoutData {
  id: string;
  containerCollections: CollectionData[];
  template?: {};
  templates?: {
    [key: string]: {
      id?: string;
      properties: TemplateProps;
    };
  };
}

export interface ContainerChildren {
  id: string;
  type: string;
}

export interface V2TemplatesData {
  pages: {};
  containers: {
    [key: string]: {
      component: {
        type: string;
        children: ContainerChildren[];
      };
    };
  };
  items: {};
}

export interface WatchHistoryItemResponse {
  data: Item;
}

export enum FormInputTypes {
  INPUT = 'input',
  PLAN = 'plan',
  STATIC = 'static',
}

export interface ChannelResponse {
  data: Item[];
  pageInfo: PageInfo;
}

export interface ScheduleType extends Item {
  schedule?: {
    startsAtEpoch?: number;
    endsAtEpoch?: number;
    startsAt?: string;
    endsAt?: string;
    playableBeforeBroadcast?: boolean;
    playableAfterBroadcast?: boolean;
  };
  display: {
    epg: {
      title: string;
      secondaryTitle: string;
    };
  };
  /**
   * Playability window. To be considered in addition to the `schedule` times. Missing means playable.
   * @see [Docs](https://docs.one.accedo.io/one-core/delivery-api/video-catalog/schedule/#playability)
   */
  playable?: {
    /** Start of the playability window. Missing means no limitation. */
    fromEpoch?: number;
    /** End of the playability window. Missing means no limitation. */
    untilEpoch?: number;
  };
}

export interface ScheduleProgressDetails {
  isLive: boolean;
  progress?: number;
  isPlayable?: boolean;
}

/** The channel of a live program, as the `live-now` endpoint returns it inside the program. */
export interface EmbeddedLiveChannel {
  id: string;
  title: string;
  channelNumber?: number;
  images: ItemImages;
}

/**
 * One entry of the `live-now` response. It is either a live channel, or the program the
 * channel plays right now. In the second case the channel is inside the program.
 * @see [Docs](https://docs.one.accedo.io/one-core/delivery-api/video-catalog/live-now/)
 */
export interface LiveNowItem extends Item {
  schedule?: ScheduleType['schedule'];
  display?: { epg?: { title?: string; secondaryTitle?: string } };
}

export interface LiveNowResponse {
  data: LiveNowItem[];
  pageInfo: PageInfo;
}

export interface ScheduleResponse {
  channel: Item;
  data: ScheduleType[];
  pageInfo: PageInfo;
}

export interface MvpdResponse {
  mvpds: Mvpd[];
}

export interface Mvpd {
  id: string;
  name: string;
  logoUrl: string;
  promoted: boolean;
}

interface DeviceNetworkInfoBase {
  deviceInfo: DeviceInfo;

  networkInfo: {
    type: 'WIFI' | 'CELLULAR';
  };
}

export interface MvpdAuthRequest
  extends DeviceNetworkInfoBase,
    AuthRefreshRequest {
  /**
   * MVPD identifier. Not providing one will result in only checking state of authentication of the user. Providing one will kickoff authorization process.
   */
  mvpdId?: string;

  /** Used when second screen authorization is used */
  authorizationCode?: string;
}

export interface MvpdAuthResponse {
  _meta: {
    type: 'success' | 'error' | 'redirect' | 'second-screen';
    redirect?: {
      targetUrl: string;
      authorizationCode: string;
    };
    secondScreen?: {
      portalUrl: string;
      authorizationCode: string;
    };
    auth?: TokenResponse;
  };
}

export interface AuthRefreshRequest {
  secondScreen?: {
    code: string;
  };
}

export interface Plan {
  id: string;
  name: string;
  purchasable: boolean;
  grantedEntitlementTags: readonly string[];
  web?: {
    productIdentifier: string;
  };
  promotionalLabel?: string;
  description?: string;
}

export interface Tier {
  id: string;
  name: string;
  purchasable: boolean;
  displayOrder: number;
  plans: Plan[];
}

export interface SubscriptionGroup {
  id: string;
  name: string;
  purchasable: boolean;
  tiers: Tier[];
}

export interface OneTimePurchase {
  id: string;
  name: string;
  purchasable: boolean;
  grantedEntitlementTags: readonly string[];
  web?: {
    productIdentifier: string;
  };
  promotionalLabel?: string;
  description?: string;
}

export interface PackageDefinition {
  subscriptionGroups: SubscriptionGroup[];
  oneTimePurchases: OneTimePurchase[];
}

export interface OrderDetails {
  paymentMethod: PurchaseMethod;
  productId?: string;
  transactionId?: string;
  timestamp?: string; // ISO String, e.g. 2024-05-10T22:17:41.813Z
  couponCodes?: (string | undefined)[];
  planSwitch?: {
    sourceSubscriptionId: string;
    sourceProductId: string;
  };
  subscriptionCancellation?: {
    subscriptionId: string;
  };
  subscriptionPurchase?: {
    subscriptionId: string;
  };
}

export interface TransactionOfferDetails {
  id: string;
  target: {
    contentObjects: { id: string; type: string }[];
  };
}

export interface ProductsResponse {
  /** Definition of all products, including currently non-purchasable ones. */
  packageDefinition?: PackageDefinition;
  /** Purchasable products, might be filtered on the server-side. */
  products?: {
    entitlements: string[];
    id: string;
  }[];
}

export interface GiftProductsResponse {
  productIds: string[];
}

export type CoreMenuConfig = {
  [FormFactors.DESKTOP]?: FormFactorMenuConfig;
  [FormFactors.MOBILE]?: FormFactorMenuConfig;
  [FormFactors.TABLET]?: FormFactorMenuConfig;
  [FormFactors.TV]?: FormFactorMenuConfig;
};

interface FormFactorMenuConfig {
  menus: MenuForFormFactor[];
}

interface MenuForFormFactor {
  id: string;
  items: MenuItem[];
}

export interface MenuItem {
  id: string;
  display: MenuItemDisplay;
  action: Action;
  visibility?: Condition;
}

interface MenuItemDisplay {
  type: MenuItemDisplayType;
  title?: string;
  icon?: IconNames;
  buttonVariant?: ButtonVariant;
}

export type Condition = {
  type: string;
  subject: string;
  value: any;
  negate?: boolean;
  conditions?: Condition[];
};

export interface SwrConfig extends SWRConfiguration {
  /* Configuration for axios fetcher */
  axios?: AxiosRequestConfig;
}

export interface UpNextResponse {
  data: Item[];
  pageInfo?: {
    hasMore: boolean;
    page: number;
    pageSize: number;
    total: number;
  };
  // this is a unique _meta, not related to the CoreResponse ones
  _meta: UpNextMeta;
}

export interface UpNextMeta {
  /** If true, the content will be played automatically after the specified countdown. */
  autoplayFirst: boolean;
  countdownSeconds: number;
  type: UpNextType;
  overlay: {
    images: {
      background: string;
      thumbnail: string;
    };
  };
}

export type UpNextType = 'BINGE' | 'UP_NEXT';
